import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import profileService from "../../services/profileService";
import { queryKeys } from "./queryKeys";

export const useProfile = () => {
  return useQuery({
    queryKey: queryKeys.profile.current(),
    queryFn: () => profileService.getProfile(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileData) => profileService.updateProfile(profileData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.current(),
      });
    },
  });
};

export const useUploadAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData) => profileService.uploadAvatar(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.current(),
      });
    },
  });
};

export const useContactInfoRequests = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.profile.contactRequests(),
    queryFn: () => profileService.getContactInfoRequests(filters),
    staleTime: 2 * 60 * 1000,
  });
};

export const useStaffProfileRequests = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.profile.staffRequests(filters),
    queryFn: () => profileService.getStaffProfileRequests(filters),
    ...options,
  });
};

export const useStudentProfileRequests = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.profile.studentRequests(filters),
    queryFn: () => profileService.getStudentProfileRequests(filters),
    ...options,
  });
};

export const useUpdateTeacherRequestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => profileService.updateTeacherRequestStatus(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.profile.all, "staffRequests"],
      });
    },
  });
};

export const useUpdateStudentRequestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => profileService.updateStudentRequestStatus(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.profile.all, "studentRequests"],
      });
    },
  });
};

export const useContactInfoRequest = (id) => {
  return useQuery({
    queryKey: [...queryKeys.profile.contactRequests(), id],
    queryFn: () => profileService.getContactInfoRequestById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePendingContactRequests = () => {
  return useQuery({
    queryKey: [...queryKeys.profile.contactRequests(), "pending"],
    queryFn: () => profileService.getPendingContactRequests(),
    staleTime: 1 * 60 * 1000,
  });
};

export const useMyContactRequests = () => {
  return useQuery({
    queryKey: [...queryKeys.profile.contactRequests(), "my"],
    queryFn: () => profileService.getMyContactRequests(),
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateContactInfoRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestData) =>
      profileService.createContactInfoRequest(requestData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.contactRequests(),
      });
    },
  });
};

export const useApproveContactInfoRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => profileService.approveContactInfoRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.contactRequests(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.current(),
      });
    },
  });
};

export const useRejectContactInfoRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) =>
      profileService.rejectContactInfoRequest(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.contactRequests(),
      });
    },
  });
};

export const useUserProfile = (userId) => {
  return useQuery({
    queryKey: [...queryKeys.profile.all, "user", userId],
    queryFn: () => profileService.getUserProfile(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, profileData }) =>
      profileService.updateUserProfile(userId, profileData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.profile.all, "user", variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.current(),
      });
    },
  });
};

export const useUpdateEmergencyContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => profileService.updateEmergencyContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.current(),
      });
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: (data) => profileService.changePassword(data),
  });
};

export const usePreferences = () => {
  return useQuery({
    queryKey: [...queryKeys.profile.all, "preferences"],
    queryFn: () => profileService.getPreferences(),
    staleTime: 10 * 60 * 1000,
  });
};

export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences) => profileService.updatePreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.profile.all, "preferences"],
      });
    },
  });
};

export const useNotificationSettings = () => {
  return useQuery({
    queryKey: [...queryKeys.profile.all, "notificationSettings"],
    queryFn: () => profileService.getNotificationSettings(),
    staleTime: 10 * 60 * 1000,
  });
};

export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings) =>
      profileService.updateNotificationSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.profile.all, "notificationSettings"],
      });
    },
  });
};

